import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Grid } from 'components/ui';
import Accordion from 'components/Accordion/Accordion';
import { MAX_PRODUCTS } from 'components/Catalog/ProductGrid/CompareModal/constants';
import { zoneStyles } from 'components/Catalog/ProductGrid/CompareModal/Zones/zoneStyles';
import { EmptyState } from 'components/Catalog/ProductGrid/hooks/useComparisonSkus';

const CompareZoneBase = ({
    skus = [], getText = () => {}, titleKey, renderContent, subsections, isCollapsed = false
}) => {
    const [isExpanded, setIsExpanded] = useState(!isCollapsed);

    const zoneId = `compare-zone-${titleKey}`;
    const title = getText(titleKey);

    const renderProductGrid = contentRenderer => (
        <Grid css={zoneStyles.productsContainer}>
            {Array.from({ length: MAX_PRODUCTS }, (_, index) => {
                const product = skus[index];
                const isEmpty = !product;
                const itemId = product?.productId || product?.parentProducts?.[0]?.productId;
                const slotKey = itemId ? `product-${itemId}` : `slot-${index}`;

                return (
                    <Box
                        key={slotKey}
                        css={zoneStyles.productSlot}
                    >
                        <Box css={zoneStyles.zoneContent}>{isEmpty ? <EmptyState /> : contentRenderer(product, index)}</Box>
                    </Box>
                );
            })}
        </Grid>
    );

    return (
        <Box css={zoneStyles.zoneSection}>
            <Accordion
                id={zoneId}
                title={title}
                isExpanded={isExpanded}
                onClick={() => setIsExpanded(!isExpanded)}
                customStyles={zoneStyles.accordionCustomStyles}
            >
                {subsections ? (
                    <Box>
                        {subsections.map((subsection, idx) => (
                            <Subsection
                                key={subsection.title || idx}
                                subsection={subsection}
                                renderProductGrid={renderProductGrid}
                            />
                        ))}
                    </Box>
                ) : (
                    renderProductGrid(renderContent)
                )}
            </Accordion>
        </Box>
    );
};

const Subsection = ({ subsection, renderProductGrid }) => {
    return (
        <Box css={zoneStyles.subsectionContainer}>
            {subsection.renderTitle ? subsection.renderTitle(subsection.type) : null}
            {renderProductGrid(subsection.renderContent)}
        </Box>
    );
};

CompareZoneBase.propTypes = {
    skus: PropTypes.array,
    getText: PropTypes.func,
    titleKey: PropTypes.string.isRequired,
    renderContent: PropTypes.func,
    subsections: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            renderContent: PropTypes.func.isRequired,
            renderTitle: PropTypes.func
        })
    )
};

Subsection.propTypes = {
    subsection: PropTypes.shape({
        title: PropTypes.string.isRequired,
        renderContent: PropTypes.func.isRequired,
        renderTitle: PropTypes.func
    }).isRequired,
    renderProductGrid: PropTypes.func.isRequired
};

export default wrapFunctionalComponent(CompareZoneBase, 'CompareZoneBase');
